import { ComponentProps, Suspense, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { FronteggProvider } from '@frontegg/react';
import {
  ErrorBoundary,
  RootProvider,
  ToastContainer,
} from '@insight-health-ai/roseate/components';
import AppRoutes from './pages/routes';
import { AppLoader } from './components/appLoader';
import { useConfigStore } from './hooks/settings';
import { logger, i18n, RUM } from 'src/services';
import { ErrorPage, ErrorScreen } from 'src/components';
import enUS from 'src/locales/en-US';
import { useTrackPerfMetrics } from './hooks/common';

logger.init();
RUM.init();
i18n.init();

const roseateLogger: ComponentProps<typeof RootProvider>['logger'] = ({
  type,
  message,
  error,
  meta,
}) => {
  logger[type](message || '[Roseate]', meta, error);
};

const fronteggProviderOptions = {
  contextOptions: {
    baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
    clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID,
  },
  hostedLoginBox: true,
  authOptions: {
    keepSessionAlive: true,
  },
};

export const App = () => {
  const { loading, getConfig, error } = useConfigStore();

  useTrackPerfMetrics();

  useEffect(() => {
    const pathSegments = window.location.pathname.split('/');
    const isPublicRoute = pathSegments[1] === 'waitlist';
    if (isPublicRoute) {
      useConfigStore.setState({ loading: false });
      return;
    }
    const chatToken = pathSegments[2];
    if (chatToken) {
      getConfig({ chatToken });
    } else {
      useConfigStore.setState({ loading: false });
    }
  }, []);

  const renderContent = () => {
    if (loading) {
      return <AppLoader />;
    }

    if (error) {
      if (error.code === 404) {
        return <ErrorPage {...enUS.auth.errors.expiredLink} />;
      }
      return <ErrorPage {...enUS.misc[500]} />;
    }

    return (
      <>
        <ToastContainer />
        <AppRoutes />
      </>
    );
  };

  return (
    <Router>
      <FronteggProvider {...fronteggProviderOptions}>
        <RootProvider
          logger={roseateLogger}
          errorBoundary={{
            fallback: (__, resetError) => (
              <ErrorScreen
                retryButton={{
                  label: 'Reload',
                  onClick: () => {
                    resetError();
                  },
                }}
              />
            ),
          }}
        >
          <Suspense fallback={<AppLoader />}>
            <ErrorBoundary
              onError={(error, errorInfo) => {
                logger.error('Lumi: Render Crash', { errorInfo }, error);
              }}
              fallback={
                <ErrorScreen
                  retryButton={{
                    label: 'Reload',
                    onClick: () => {
                      window.location.reload();
                    },
                  }}
                />
              }
            >
              {renderContent()}
            </ErrorBoundary>
          </Suspense>
        </RootProvider>
      </FronteggProvider>
    </Router>
  );
};
